import React from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

// ---- Curated tag palette ------------------------------------------------
// Each tag has a unique colour; hues are grouped by semantic meaning
// (greens = positive / opportunity, yellows-golds = elite / quality,
// oranges = form / heat, reds = risk / warning, purples-blues = differential
// / cool / dormant). Labels stripped of emoji for a consistent typographic
// rhythm — the colour + tooltip carry the meaning.
export const TAG_STYLES = {
    // --- Positive / opportunity (greens & teals) ---
    SAFE_PICK:           { bg: "#9EE8B4", text: "#050505", label: "Safe Pick" },
    FAVOURITE_OPPONENT:  { bg: "#00C896", text: "#050505", label: "Favourite Opponent" },
    BUY_LOW:             { bg: "#5EEAD4", text: "#050505", label: "Buy Low" },

    // --- Premium / Quality (yellows & golds) ---
    PREMIUM_TRUST:       { bg: "#FFF3C2", text: "#050505", label: "Premium Trust" },
    DATA_ANALYST:        { bg: "#FFD180", text: "#050505", label: "Data Analyst" },
    BIG_GAME_PLAYER:     { bg: "#FFB800", text: "#050505", label: "Big-Game Player" },

    // --- Hot / Form (oranges) ---
    FORM_HUNTER:         { bg: "#FF6B35", text: "#050505", label: "Form Hunter" },
    CHAOS_MANAGER:       { bg: "#FF8C00", text: "#050505", label: "Chaos Manager" },

    // --- Risk / Warning (reds) ---
    REGRESSION_RISK:     { bg: "#FF2A2A", text: "#ffffff", label: "Regression Risk" },
    BAD_MATCHUP:         { bg: "#B22222", text: "#ffffff", label: "Bad Matchup" },
    FDR_TRAP:            { bg: "#E94560", text: "#ffffff", label: "FDR Trap" },

    // --- Differential / Cool (purples & blues) ---
    DIFFERENTIAL_KING:   { bg: "#A855F7", text: "#ffffff", label: "Differential King" },
    BREAKOUT_PICK:       { bg: "#7C3AED", text: "#ffffff", label: "Breakout Pick" },
    COLD_STAR:           { bg: "#6FA8DC", text: "#050505", label: "Cold Star" },

    // --- Qualification modifier flags (injected by news_signals + qualification_modifiers) ---
    MUST_WIN:            { bg: "#FF453A", text: "#ffffff", label: "Must Win" },
    ROTATION_RISK:       { bg: "#94A3B8", text: "#050505", label: "Rotation Risk" },
};

export const TAG_DESCRIPTIONS = {
    DIFFERENTIAL_KING:
        "Low ownership (<15%) + solid returns. High-variance pick that rewards bold managers while the template ignores him.",
    PREMIUM_TRUST:
        "High-price, high-ceiling asset trusted by top managers. Low variance, reliable points week after week.",
    COLD_STAR:
        "Elite player currently out of form. Watch for a bounce — the template may offload him right before he explodes.",
    SAFE_PICK:
        "Strong minutes security + consistent points. The floor is high; points variance is low.",
    CHAOS_MANAGER:
        "Unpredictable output. Big hauls mixed with blanks. Use for explosive GWs, avoid if you want stability.",
    FORM_HUNTER:
        "Red-hot recent form (last 4-5 GWs). Backed by xGI/goals — more than a lucky streak.",
    BREAKOUT_PICK:
        "Emerging player with a rising trend: underlying stats outpacing his points. Likely about to get ownership flood.",
    DATA_ANALYST:
        "Strong underlying metrics (xG, xA, defensive_contribution) even if raw points don't yet reflect it. A bet on regression to the mean.",
    FAVOURITE_OPPONENT:
        "Historically loves the next opponent: points-per-match vs them is significantly above his career average (Δ ≥ +1.5 ppm).",
    BAD_MATCHUP:
        "Historically struggles against the next opponent: points-per-match Δ ≤ -1.5 ppm vs career average. Consider avoiding.",
    REGRESSION_RISK:
        "Heavily overperforming his threat (>2.5 extra goals above expected). Expect a cold streak — sell high or avoid captaincy.",
    BUY_LOW:
        "Underperforming his threat (>2 goals below expected) despite low ownership. Statistical regression suggests points coming — get in early.",
    FDR_TRAP:
        "FPL's FDR rates his next fixtures as hard, but the player's historical record against those opponents is strong. The FDR is lying — back the history.",
    BIG_GAME_PLAYER:
        "Talisman with a strong historical record against an opponent involved in a title / relegation / Europe six-pointer in his next window. Expect them to show up when stakes are highest.",
    MUST_WIN:
        "His team is in a high-stakes race (title contention, European push or relegation battle) where the final games are effectively must-win. Attackers are usually given more freedom — boost expected.",
    ROTATION_RISK:
        "His team has already secured European qualification or is mathematically locked. Manager may rest first-team players against weaker opponents — minutes confidence drops.",
};

export function PlayerTag({ tag, description }) {
    const style = TAG_STYLES[tag] || { bg: "#1E1E1E", text: "#ffffff", label: tag };
    const desc = description || TAG_DESCRIPTIONS[tag];
    const chip = (
        <span
            className="font-tag text-[10px] font-bold uppercase px-1.5 py-0.5 inline-flex items-center leading-none cursor-help"
            style={{ background: style.bg, color: style.text }}
            data-testid={`tag-${tag.toLowerCase().replace(/_/g, "-")}`}
        >
            {style.label}
        </span>
    );
    if (!desc) return chip;
    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>{chip}</TooltipTrigger>
                <TooltipContent
                    side="top"
                    className="max-w-[260px] bg-black border border-white/20 text-white rounded-none font-mono text-[11px] leading-snug tracking-normal px-3 py-2"
                >
                    <div className="font-display font-black uppercase text-[10px] mb-1" style={{ color: style.bg }}>
                        {style.label}
                    </div>
                    <div className="text-white/80">{desc}</div>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

export function QualityBadge({ score }) {
    let color = "#FF2A2A";
    let label = "LOW";
    if (score >= 80) {
        color = "#9EE8B4";
        label = "HIGH";
    } else if (score >= 70) {
        color = "#FFF3C2";
        label = "MED";
    }
    return (
        <div
            className="font-mono text-[10px] font-bold uppercase tracking-widest border px-1.5 py-0.5 inline-flex items-center gap-1 leading-none"
            style={{ color, borderColor: `${color}80`, background: `${color}10` }}
            data-testid={`quality-badge-${score}`}
        >
            <span>Q</span>
            <span>{score}</span>
            <span className="opacity-60">· {label}</span>
        </div>
    );
}

export function TypeBadge({ type }) {
    return (
        <span
            className="font-mono text-[10px] font-bold uppercase tracking-widest border border-white/20 bg-white/5 text-white px-2 py-0.5 leading-none"
            data-testid={`type-badge-${type.toLowerCase()}`}
        >
            {type.replace("_", " ")}
        </span>
    );
}

export function StatusPill({ status }) {
    const STYLES = {
        published: { color: "#9EE8B4", label: "published" },
        ready_for_review: { color: "#9DB7F5", label: "ready for review" },
        community_review: { color: "#FFF3C2", label: "community" },
        archived: { color: "#6B7280", label: "archived" },
        draft: { color: "#A1A1AA", label: "draft" },
    };
    const st = STYLES[status] || STYLES.draft;
    return (
        <span
            className="font-mono text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1.5 leading-none"
            style={{ color: st.color }}
            data-testid={`status-${status}`}
        >
            <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: st.color, boxShadow: `0 0 8px ${st.color}` }}
            />
            {st.label}
        </span>
    );
}
